import React, { createContext, Fragment, useEffect, useState } from 'react';
import _, { omit } from 'lodash';
import { useHistory, useParams } from 'react-router-dom';
import uuid from 'uuid';

import { MembersTab } from 'components/organisms/CourseDashboard';
import { TeamsTab } from 'components/organisms/Teams';
import { useSnackbar } from 'contexts/snackbar';
import { useUser2 } from 'contexts/user2';
import { useWebsocket } from 'contexts/websocket';
import accountService from 'services/account';
import batchService from 'services/batch';
import courseService from 'services/course';
import { Text } from 'shell/components';
import { downloadCSV, parseCSV } from 'utils/helper';

// content is initialized as empty fragment and resassigned later so that
// the order of declaration can be maintained
const OPTIONS = [
    { key: 'members', value: 'Members', content: <></> },
    { key: 'teams', value: 'Teams', content: <></> },
];

const CSV_HEADERS = ['first name', 'last name', 'email', 'team number'];

const PageContext = createContext();

const PageProvider = (props) => {
    const history = useHistory();
    const params = useParams();
    const { message } = useSnackbar();
    const {
        course,
        groups,
        accountUsers,
        studentAccountUsers,
        addAccountUser,
        removeAccountUser,
        updateGroupsAndAccounts,
    } = useUser2();
    const { sendOn, teamWS, setTeamWS } = useWebsocket();

    // teams page state
    const [tab, setTab] = useState(OPTIONS[0].key);
    const [isWSLoading, setIsWSLoading] = useState(false); // loading state ws action
    const [dialog, setDialog] = useState({
        open: false,
        message: '',
        description: '',
        variant: 'primary',
        actionButtonText: '',
        onAction: () => {},
    });

    // members tab state
    const [checked, setChecked] = useState([]);
    const [isInviteInstructorOpen, setIsInviteInstructorOpen] = useState(false);
    const [inviteInstructorInputValue, setInviteInstructorInputValue] = useState('');
    const [inviteInstructorEmails, setInviteInstructorEmails] = useState([]);
    const [isInviteInstructorLoading, setIsInviteInstructorLoading] = useState(false);
    const [isInviteStudentOpen, setIsInviteStudentOpen] = useState(false);
    const [inviteStudentAccordions, setInviteStudentAccordions] = useState({ staged: true, invited: false });
    const [csvStudents, setCSVStudents] = useState([]);
    const [inviteStudentInputValue, setInviteStudentInputValue] = useState('');
    const [inviteStudentEmails, setInviteStudentEmails] = useState([]);
    const [isInviteStudentLoading, setIsInviteStudentLoading] = useState(false);
    const [syncRosterDialog, setSyncRosterDialog] = useState({ open: false });
    const [memberFilters, setMemberFilters] = useState({
        open: false,
        sortKey: 'namea-z',
        filterKeys: [],
        searchInput: '',
    });

    // teams tab state
    const [isLoading, setIsLoading] = useState(true);
    const [isCreateFromScratchOpen, setIsCreateFromScratchOpen] = useState(false);
    const [isGroupFormationOpen, setIsGroupFormationOpen] = useState(false);
    const [syncGroupDialog, setSyncGroupDialog] = useState({ open: false });
    const [groupFormation, setGroupFormation] = useState(null);
    const [surveys, setSurveys] = useState(null);
    const [editTeams, setEditTeams] = useState({
        open: false,
        type: '',
        groupSize: '',
        numGroups: '',
        groups,
        studentAccountUsers,
        onSubmit: () => {},
    });

    const handleChangeTab = (tab) => history.push(`/people/${tab}`);

    const sendInvitations = async (type, users) => {
        const addedUsers = await batchService.batchSendInvitations({
            data: {
                is_lti: false,
                root_url: window.location.origin,
                course_id: course.id,
                type,
                users: users.map((user) => {
                    const { email, first_name = '', last_name = '' } = user;
                    let accountUser = accountUsers.find((accountUser) => accountUser.email === email);
                    if (!accountUser) {
                        accountUser = {
                            owner_account_id: uuid.v4(),
                            user_id: uuid.v4(),
                            active: true,
                            created_on: new Date().getTime(),
                            role: type,
                            email: email?.toLowerCase(),
                            first_name,
                            last_name,
                        };
                    }
                    return {
                        id: accountUser.owner_account_id,
                        user_id: accountUser.user_id,
                        email: accountUser.email,
                        first_name: accountUser.first_name,
                        last_name: accountUser.last_name,
                    };
                }),
            },
        });
        addedUsers.forEach((user) => {
            const accountUser = accountUsers.find(({ user_id }) => user_id === user.id);
            if (!accountUser) {
                addAccountUser({
                    ...omit(user, ['id', 'account_id']),
                    owner_account_id: user.account_id,
                    user_id: user.id,
                });
            }
        });
    };

    const removeUser = async (accountUser) => {
        removeAccountUser(accountUser);
        await accountService.deleteAccount(accountUser.owner_account_id);
    };

    const getLowestUniqueGroupNumber = (groups) => {
        for (let i = 1; i <= groups.length; i++) {
            if (!groups.find(({ number }) => number === i.toString())) {
                return i.toString();
            }
        }
        return (groups.length + 1).toString();
    };

    const downloadTeamCSV = () => {
        const isLti = process.env.REACT_APP_AUTH === 'LTI';
        const data = (studentAccountUsers || []).map((student) => {
            const group = groups.find((group) => group.id === student.group_id);
            return [
                student.display_name || student.first_name || '',
                student.last_name || '',
                isLti ? student.username : student.email,
                group?.number,
            ];
        });
        return downloadCSV(`${course.name}_teams_template.csv`.toLowerCase().replaceAll(' ', '_'), [
            CSV_HEADERS,
            ...data,
        ]);
    };

    const uploadTeamCSV = async (e) => {
        const hasMissingEmail = (parsed) => {
            return parsed.some((row) => {
                const email = row[2];
                return !email;
            });
        };

        const getStudentNotInCourse = (parsed) => {
            const students = parsed.map((row) => {
                return { email: row[2] };
            });
            return students.filter((student) => {
                return !studentAccountUsers.find(
                    (accountUser) => accountUser.email === student.email || accountUser.username === student.email
                );
            });
        };

        const hasInvalidGroupNumber = (parsed) => {
            const isAscendingIntegerArray = (arr) => {
                let expected = 1;
                return arr.every((num) => num === expected++);
            };
            const groupNumbers = [
                ...new Set(
                    parsed
                        .map((row) => row[3])
                        .filter((x) => x)
                        .map((x) => Number(x))
                ),
            ].sort();
            return !isAscendingIntegerArray(groupNumbers);
        };

        const getChanges = (groups, studentAccountUsers, parsed) => {
            const getTeamChanges = () => {
                const deletes = groups.map(({ id }) => ({
                    type: 'group',
                    action: 'delete',
                    payload: { id },
                }));
                const creates = [
                    ...new Set(
                        parsed
                            .map((row) => row[3])
                            .filter((x) => x)
                            .map((x) => Number(x))
                    ),
                ]
                    .sort()
                    .map((groupNumber) => ({
                        type: 'group',
                        action: 'create',
                        payload: {
                            id: uuid.v4(),
                            name: `Team ${groupNumber}`,
                            number: groupNumber.toString(),
                        },
                    }));
                return [...deletes, ...creates];
            };

            const getStudentChanges = (teamChanges) => {
                return parsed.map((row) => {
                    const studentAccountUser = studentAccountUsers.find(({ email, username }) => {
                        return email === row[2] || username === row[2];
                    });
                    const newGroup = teamChanges.find((change) => {
                        return change.action === 'create' && change.payload.number.toString() === row[3].toString();
                    });
                    return {
                        type: 'member',
                        action: 'move',
                        payload: {
                            id: studentAccountUser.user_id,
                            from: studentAccountUser.group_id,
                            to: newGroup?.payload?.id || null,
                        },
                    };
                });
            };

            const teamChanges = getTeamChanges();
            const studentChanges = getStudentChanges(teamChanges);

            return {
                teamChanges: teamChanges,
                studentChanges: studentChanges,
            };
        };

        let parsed;

        try {
            parsed = await parseCSV(e.target.files[0], CSV_HEADERS, message);
        } catch (e) {
            return console.error(e);
        }

        if (hasMissingEmail(parsed)) {
            return message.error('One or more students have missing emails.');
        }

        const studentsNotInCourse = getStudentNotInCourse(parsed);
        if (new Set(studentsNotInCourse).size) {
            return message.error(
                <Text color={'inherit'}>
                    The following student(s) are not in this course:{' '}
                    {[...new Set(studentsNotInCourse)].map(({ first_name, last_name }, index) => (
                        <Fragment key={index}>
                            {index ? ', ' : null}
                            <Text color={'inherit'} component={'span'} weight={'semibold'}>
                                {`${first_name} ${last_name}`}
                            </Text>
                        </Fragment>
                    ))}
                </Text>
            );
        }

        if (hasInvalidGroupNumber(parsed)) {
            return message.error('Group numbers must be integers in ascending order starting from 1.');
        }

        const { teamChanges, studentChanges } = getChanges(groups, studentAccountUsers, parsed);
        setDialog((current) => ({ ...current, open: false, onAction: () => {} }));
        setIsWSLoading(true);
        sendOn('default', {
            payload_type: 'update_user_teams',
            payload: {
                course_id: course.id,
                team_changes: teamChanges,
                student_changes: studentChanges,
            },
        });
    };

    const handleSyncRoster = async (isGroupSyncing) => {
        return setSyncRosterDialog({ open: true, isGroupSyncing: isGroupSyncing });
    };

    const sendGroupSetUpdate = async (groupSets) => {
        const getChanges = (groups, studentAccountUsers, incomingGroupSyncData) => {
            const getTeamChanges = () => {
                const deletes = groups.map(({ id }) => ({
                    type: 'group',
                    action: 'delete',
                    payload: { id },
                }));
                const creates = [
                    ...new Set(
                        incomingGroupSyncData
                            .filter((group) => group.groupNumber)
                            .map((group) => ({
                                name: group.name || `Team ${group.groupNumber}`,
                                number: Number(group.groupNumber),
                            }))
                    ),
                ]
                    .sort((a, b) => a.number - b.number)
                    .map((group) => {
                        return {
                            type: 'group',
                            action: 'create',
                            payload: {
                                id: uuid.v4(),
                                name: group.name,
                                number: group.number.toString(),
                            },
                        };
                    });
                return [...deletes, ...creates];
            };

            const getStudentChanges = (teamChanges, incomingGroupSyncData) => {
                const studentChanges = incomingGroupSyncData?.reduce((acc, groupSet) => {
                    const studentChange = groupSet.members?.map((member) => {
                        const findUser = studentAccountUsers.find((student) => student.user_id === member.user_id);
                        const newGroup = teamChanges.find((change) => {
                            return (
                                change.action === 'create' &&
                                change.payload.number.toString() === groupSet.groupNumber.toString()
                            );
                        });
                        if (findUser) {
                            return {
                                type: 'member',
                                action: 'move',
                                payload: {
                                    id: findUser.user_id,
                                    from: findUser.group_id || null,
                                    to: newGroup?.payload?.id || null,
                                },
                            };
                        } else {
                            return null;
                        }
                    });
                    acc = [...acc, ...studentChange.filter((student) => student)];
                    return acc;
                }, []);
                return studentChanges;
            };

            const teamChanges = getTeamChanges();
            const studentChanges = getStudentChanges(teamChanges, groupSets.data);

            return {
                teamChanges: teamChanges,
                studentChanges: studentChanges,
            };
        };

        const { teamChanges, studentChanges } = getChanges(groups, studentAccountUsers, groupSets.data);
        setIsWSLoading(true);
        sendOn('default', {
            payload_type: 'update_user_teams',
            payload: {
                course_id: course.id,
                team_changes: teamChanges,
                student_changes: studentChanges,
            },
        });
    };

    useEffect(() => {
        if (!params.tab) {
            history.replace(`/people/${tab}`);
        }
        setTab(params.tab);
    }, [params.tab, tab]);

    useEffect(() => {
        if (!teamWS) return;
        const { payload_type, payload } = teamWS;
        if (payload_type === 'updated_teams') {
            const { groups, accounts } = payload;
            updateGroupsAndAccounts(groups, accounts);
            setIsWSLoading(false);
            message.success('Changes to teams have been saved successfully!');
            setTeamWS(null);
        }
    }, [teamWS]);

    useEffect(() => {
        if (!course) return;
        (async () => {
            const groupFormation = await courseService.listCourseGroupFormations(course.id);
            setGroupFormation(groupFormation);
            if (groupFormation) {
                const surveys = await courseService.listCourseGroupSurveys(
                    course.id,
                    JSON.stringify({ group_formation_id: groupFormation.id })
                );
                setSurveys(surveys);
            } else {
                setSurveys([]);
            }
            setIsLoading(false);
        })();
    }, [course?.id]);

    OPTIONS[0].content = (
        <MembersTab
            isInCourseDashboard={false}
            course={course}
            accountUsers={accountUsers?.filter((accountUser) => accountUser.active)}
            sendInvitations={sendInvitations}
            removeUser={removeUser}
        />
    );
    OPTIONS[1].content = <TeamsTab />;

    const value = {
        options: OPTIONS,
        tab,
        setTab: handleChangeTab,

        // shared between members and teams tab
        isWSLoading,
        setIsWSLoading,
        dialog,
        setDialog,

        // members tab
        checked,
        setChecked,
        isInviteInstructorOpen,
        setIsInviteInstructorOpen,
        inviteInstructorInputValue,
        setInviteInstructorInputValue,
        inviteInstructorEmails,
        setInviteInstructorEmails,
        isInviteInstructorLoading,
        setIsInviteInstructorLoading,
        isInviteStudentOpen,
        setIsInviteStudentOpen,
        inviteStudentAccordions,
        setInviteStudentAccordions,
        csvStudents,
        setCSVStudents,
        inviteStudentInputValue,
        setInviteStudentInputValue,
        inviteStudentEmails,
        setInviteStudentEmails,
        isInviteStudentLoading,
        setIsInviteStudentLoading,
        syncRosterDialog,
        setSyncRosterDialog,
        handleSyncRoster,
        memberFilters,
        setMemberFilters,

        // teams tab
        isLoading,
        isCreateFromScratchOpen,
        setIsCreateFromScratchOpen,
        isGroupFormationOpen,
        setIsGroupFormationOpen,
        syncGroupDialog,
        setSyncGroupDialog,
        groupFormation,
        setGroupFormation,
        surveys,
        setSurveys,
        editTeams,
        setEditTeams,
        getLowestUniqueGroupNumber,
        downloadTeamCSV,
        uploadTeamCSV,
        sendGroupSetUpdate,
    };

    return <PageContext.Provider value={value} {...props} />;
};

export { PageContext };
export default PageProvider;
